import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50 dark:bg-gray-900" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = { class: "flex items-center space-x-3" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "p-6 space-y-6" }
const _hoisted_6 = { class: "flex flex-wrap items-center gap-4" }
const _hoisted_7 = { class: "flex items-center space-x-2" }
const _hoisted_8 = { class: "flex items-center space-x-2" }
const _hoisted_9 = ["value"]
const _hoisted_10 = { class: "flex items-center space-x-2" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "flex items-center space-x-2" }
const _hoisted_13 = ["value"]
const _hoisted_14 = { class: "text-sm text-red-700 dark:text-red-200" }
const _hoisted_15 = {
  key: 2,
  class: "space-y-4"
}
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "p-4" }
const _hoisted_18 = { class: "flex items-center justify-between" }
const _hoisted_19 = { class: "flex items-center space-x-4" }
const _hoisted_20 = { class: "mr-1" }
const _hoisted_21 = { class: "flex items-center space-x-2" }
const _hoisted_22 = { class: "font-mono text-sm font-medium text-gray-900 dark:text-white" }
const _hoisted_23 = { class: "text-gray-500 dark:text-gray-400" }
const _hoisted_24 = { class: "text-sm text-gray-500 dark:text-gray-400" }
const _hoisted_25 = {
  key: 0,
  class: "flex justify-center pt-2"
}
const _hoisted_26 = ["disabled"]

import { inject, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { qualityGatesService, leaderboardService } from '../../services/quality-gates'
import type { QualityGatePR, TimeRange } from '../../types/quality-gates'
import { useTheme } from '../../utils/useTheme'


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const theme = inject('theme', useTheme())

const prs = ref<QualityGatePR[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const pagination = ref({
  hasMore: false,
  nextCursor: undefined as string | undefined
})
const teams = ref<string[]>([])
const subTeams = ref<string[]>([])
const qualityGates = ref<string[]>([])

const team = ref('')
const subTeam = ref('')
const qualityGate = ref('')
const timeRange = ref<TimeRange>('7d')

const updateURL = () => {
  const query: Record<string, string> = {
    team: team.value,
    timeRange: timeRange.value
  }
  if (subTeam.value) query.subTeam = subTeam.value
  if (qualityGate.value) query.qualityGate = qualityGate.value
  router.replace({ query })
}

const fetchCompliancePRs = async (append = false) => {
  if (!team.value) {
    error.value = 'Team is required for compliance PR listing.'
    prs.value = []
    pagination.value = { hasMore: false, nextCursor: undefined }
    return
  }

  try {
    loading.value = true
    error.value = null
    if (!append) {
      updateURL()
    }
    const response = await qualityGatesService.fetchCompliancePRs({
      team: team.value,
      subTeam: subTeam.value || undefined,
      qualityGate: qualityGate.value || undefined,
      timeRange: timeRange.value,
      limit: 20,
      cursor: append ? pagination.value.nextCursor : undefined
    })
    if (append) {
      prs.value.push(...response.data)
    } else {
      prs.value = response.data
    }
    pagination.value = {
      hasMore: response.pagination.hasMore,
      nextCursor: response.pagination.nextCursor
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load PRs'
    prs.value = []
    pagination.value = { hasMore: false, nextCursor: undefined }
  } finally {
    loading.value = false
  }
}

const fetchFilterOptions = async () => {
  try {
    const [teamsData, gatesData] = await Promise.all([
      leaderboardService.fetchTeams(),
      leaderboardService.fetchQualityGates()
    ])
    teams.value = teamsData.teams
    subTeams.value = teamsData.subTeams
    qualityGates.value = gatesData.gates

    if (!team.value && teams.value.length > 0) {
      team.value = teams.value[0]
    }
  } catch {
    // keep current values; page can still function with URL provided filters
  }
}

const onTeamChange = () => {
  subTeam.value = ''
  fetchCompliancePRs()
}

const handleFilterChange = () => {
  void fetchCompliancePRs()
}

const handleRefresh = () => {
  void fetchCompliancePRs()
}

const loadMore = () => {
  if (!pagination.value.hasMore || !pagination.value.nextCursor || loading.value) return
  fetchCompliancePRs(true)
}

const navigateToPR = (pr: QualityGatePR) => {
  router.push(`/quality-gates/${encodeURIComponent(pr.repository)}/${pr.pr_number}`)
}

const goBack = () => {
  const query: Record<string, string> = {
    team: team.value,
    timeRange: timeRange.value
  }

  if (subTeam.value) query.subTeam = subTeam.value
  if (qualityGate.value) query.qualityGate = qualityGate.value

  router.push({
    path: '/quality-gates/compliance/details',
    query
  })
}

onMounted(() => {
  team.value = (route.query.team as string) || ''
  subTeam.value = (route.query.subTeam as string) || ''
  qualityGate.value = (route.query.qualityGate as string) || ''
  timeRange.value = ((route.query.timeRange as TimeRange) || '7d') as TimeRange
  fetchFilterOptions()
  fetchCompliancePRs()
})

watch(
  () => route.query,
  (newQuery) => {
    const nextTeam = (newQuery.team as string) || ''
    const nextSubTeam = (newQuery.subTeam as string) || ''
    const nextQualityGate = (newQuery.qualityGate as string) || ''
    const nextTimeRange = ((newQuery.timeRange as TimeRange) || '7d') as TimeRange

    const queryChanged =
      nextTeam !== team.value ||
      nextSubTeam !== subTeam.value ||
      nextQualityGate !== qualityGate.value ||
      nextTimeRange !== timeRange.value

    if (queryChanged) {
      team.value = nextTeam
      subTeam.value = nextSubTeam
      qualityGate.value = nextQualityGate
      timeRange.value = nextTimeRange
      fetchCompliancePRs()
    }
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            class: _normalizeClass([
              _unref(theme).isDark.value
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
              'p-2 rounded-lg transition-all'
            ]),
            title: "Back to Compliance Breakdown",
            onClick: goBack
          }, _cache[4] || (_cache[4] = [
            _createElementVNode("svg", {
              class: "w-5 h-5",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M10 19l-7-7m0 0l7-7m-7 7h18"
              })
            ], -1)
          ]), 2),
          _createElementVNode("div", null, [
            _createElementVNode("h1", {
              class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold'])
            }, " Compliance PRs ", 2),
            _createElementVNode("p", {
              class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm'])
            }, " Pull requests in the selected compliance scope ", 2)
          ])
        ]),
        _createElementVNode("button", {
          disabled: loading.value,
          class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-white hover:bg-gray-50 text-gray-700',
            'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors flex items-center',
            loading.value ? 'opacity-50 cursor-not-allowed' : ''
          ]),
          onClick: handleRefresh
        }, [
          (_openBlock(), _createElementBlock("svg", {
            class: _normalizeClass([loading.value ? 'animate-spin' : '', 'w-4 h-4 mr-2']),
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24"
          }, _cache[5] || (_cache[5] = [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            }, null, -1)
          ]), 2)),
          _createTextVNode(" " + _toDisplayString(loading.value ? 'Loading...' : 'Refresh'), 1)
        ], 10, _hoisted_4)
      ])
    ], 2),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", {
        class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-md p-3'
        ])
      }, [
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", {
              class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium'])
            }, " Time Range: ", 2),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timeRange).value = $event)),
              class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]),
              onChange: handleFilterChange
            }, _cache[6] || (_cache[6] = [
              _createElementVNode("option", { value: "7d" }, "Last 7 Days", -1),
              _createElementVNode("option", { value: "14d" }, "Last 14 Days", -1),
              _createElementVNode("option", { value: "30d" }, "Last 30 Days", -1)
            ]), 34), [
              [_vModelSelect, timeRange.value]
            ])
          ]),
          _createElementVNode("div", {
            class: _normalizeClass([_unref(theme).isDark.value ? 'bg-gray-700' : 'bg-gray-300', 'w-px h-6'])
          }, null, 2),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("span", {
              class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium'])
            }, " Team: ", 2),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((team).value = $event)),
              class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]),
              onChange: onTeamChange
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(teams.value, (teamName) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: teamName,
                  value: teamName
                }, _toDisplayString(_unref(leaderboardService).formatTeamName(teamName)), 9, _hoisted_9))
              }), 128))
            ], 34), [
              [_vModelSelect, team.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("span", {
              class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium'])
            }, " Sub-Team: ", 2),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((subTeam).value = $event)),
              class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]),
              onChange: handleFilterChange
            }, [
              _cache[7] || (_cache[7] = _createElementVNode("option", { value: "" }, "All Sub-Teams", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subTeams.value, (subTeamName) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: subTeamName,
                  value: subTeamName
                }, _toDisplayString(_unref(leaderboardService).formatSubTeamName(subTeamName)), 9, _hoisted_11))
              }), 128))
            ], 34), [
              [_vModelSelect, subTeam.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("span", {
              class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium'])
            }, " Quality Gate: ", 2),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((qualityGate).value = $event)),
              class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]),
              onChange: handleFilterChange
            }, [
              _cache[8] || (_cache[8] = _createElementVNode("option", { value: "" }, "All Gates", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(qualityGates.value, (gate) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: gate,
                  value: gate
                }, _toDisplayString(_unref(leaderboardService).formatGateName(gate)), 9, _hoisted_13))
              }), 128))
            ], 34), [
              [_vModelSelect, qualityGate.value]
            ])
          ])
        ])
      ], 2),
      (error.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ])
          }, [
            _createElementVNode("div", _hoisted_14, _toDisplayString(error.value), 1)
          ], 2))
        : _createCommentVNode("", true),
      (loading.value && prs.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ])
          }, _cache[9] || (_cache[9] = [
            _createStaticVNode("<div class=\"flex items-center justify-center\"><svg class=\"animate-spin h-8 w-8 text-blue-500\" fill=\"none\" viewBox=\"0 0 24 24\"><circle class=\"opacity-25\" cx=\"12\" cy=\"12\" r=\"10\" stroke=\"currentColor\" stroke-width=\"4\"></circle><path class=\"opacity-75\" fill=\"currentColor\" d=\"M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z\"></path></svg><span class=\"ml-3 text-lg text-gray-600 dark:text-gray-300\">Loading PRs...</span></div>", 1)
          ]), 2))
        : (prs.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prs.value, (pr) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: `${pr.repository}-${pr.pr_number}`,
                  class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer',
            `border-l-4 ${_unref(qualityGatesService).getStatusBorderColor(pr.status)}`
          ]),
                  onClick: ($event: any) => (navigateToPR(pr))
                }, [
                  _createElementVNode("div", _hoisted_17, [
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("span", {
                          class: _normalizeClass([
                    _unref(qualityGatesService).getStatusColor(pr.status),
                    'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium'
                  ])
                        }, [
                          _createElementVNode("span", _hoisted_20, _toDisplayString(_unref(qualityGatesService).getStatusIcon(pr.status)), 1),
                          _createTextVNode(" " + _toDisplayString(pr.status.toUpperCase()), 1)
                        ], 2),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_21, [
                            _createElementVNode("span", _hoisted_22, _toDisplayString(pr.repository), 1),
                            _createElementVNode("span", _hoisted_23, "#" + _toDisplayString(pr.pr_number), 1)
                          ]),
                          _createElementVNode("div", _hoisted_24, _toDisplayString(_unref(qualityGatesService).formatRelativeTime(pr.last_updated)) + " · " + _toDisplayString(pr.commit_sha.substring(0, 7)), 1)
                        ])
                      ]),
                      _cache[10] || (_cache[10] = _createElementVNode("svg", {
                        class: "w-5 h-5 text-gray-400",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M9 5l7 7-7 7"
                        })
                      ], -1))
                    ])
                  ])
                ], 10, _hoisted_16))
              }), 128)),
              (pagination.value.hasMore)
                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                    _createElementVNode("button", {
                      disabled: loading.value,
                      class: _normalizeClass([
              _unref(theme).isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors',
              loading.value ? 'opacity-50 cursor-not-allowed' : ''
            ]),
                      onClick: loadMore
                    }, _toDisplayString(loading.value ? 'Loading...' : 'Load More'), 11, _hoisted_26)
                  ]))
                : _createCommentVNode("", true)
            ]))
          : (!loading.value && !error.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 3,
                class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8 text-center'
        ])
              }, _cache[11] || (_cache[11] = [
                _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-900 dark:text-white" }, "No results found", -1),
                _createElementVNode("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, " No pull requests match the selected compliance scope. ", -1)
              ]), 2))
            : _createCommentVNode("", true)
    ])
  ]))
}
}

})